import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const HomePage = () => {
  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Lower Energy Bills',
      description: 'Reduce your monthly electricity costs by up to 70% with solar energy and start saving from day one.',
    },
    {
      icon: 'ri-leaf-line',
      title: 'Environmental Impact',
      description: 'Reduce your carbon footprint and contribute to a cleaner, more sustainable future for Florida.',
    },
    {
      icon: 'ri-home-smile-line',
      title: 'Increase Home Value',
      description: 'Solar panels can increase your property value by an average of 4% while providing energy independence.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Tax Incentives',
      description: 'Take advantage of federal tax credits and state incentives that make solar more affordable than ever.',
    },
  ];

  const steps = [
    {
      number: '01',
      title: 'Free Consultation',
      description: 'We assess your energy needs and design a custom solar solution for your property.',
    },
    {
      number: '02',
      title: 'Professional Installation',
      description: 'Our certified technicians install your system with precision and care, ensuring optimal performance.',
    },
    {
      number: '03',
      title: 'Start Saving',
      description: 'Begin enjoying lower energy bills and energy independence immediately after activation.',
    },
  ];

  const testimonials = [
    {
      name: 'Michael Thompson',
      location: 'Ocala, FL',
      rating: 5,
      text: 'BRICK CITY SOLAR made the entire process seamless. Our energy bills have dropped by 65% and the system looks great on our roof!',
    },
    {
      name: 'Sarah Martinez',
      location: 'Ocala, FL',
      rating: 5,
      text: 'Professional, knowledgeable, and reliable. They answered all our questions and delivered exactly what they promised. Highly recommend!',
    },
    {
      name: 'David Chen',
      location: 'Ocala, FL',
      rating: 5,
      text: 'Best investment we\'ve made for our home. The team was fantastic and the savings are real. Thank you BRICK CITY SOLAR!',
    },
  ];

  return (
    <div className="font-poppins">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/5c03691e8c44183793c7e06ac12fec0f.jpg"
            alt="Solar panels at sunrise"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-solar-blue/70 via-solar-blue/50 to-solar-blue/70"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
          >
            Clean Energy. Lower Bills.<br />
            <span className="text-energy-yellow">Energy Independence.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto"
          >
            Power your home or business with professional solar solutions from Ocala's trusted solar experts.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link
              to="/contact"
              className="bg-energy-yellow text-solar-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-all shadow-2xl cursor-pointer whitespace-nowrap"
            >
              Get Free Solar Quote
            </Link>
            <a
              href="tel:352-509-6933"
              className="bg-white text-solar-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all shadow-2xl cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Call Now
            </a>
          </motion.div>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-3xl"></i>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-4">
              Why Go Solar in <strong>Ocala, Florida</strong>?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover the powerful benefits of switching to clean, renewable solar energy for your home or business.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-soft-gray p-8 rounded-xl hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-energy-yellow rounded-lg mb-6">
                  <i className={`${benefit.icon} text-3xl text-solar-blue`}></i>
                </div>
                <h3 className="text-xl font-bold text-solar-blue mb-3">{benefit.title}</h3>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How Solar Works */}
      <section className="py-20 bg-soft-gray">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-4">
              How <strong>Solar Installation</strong> Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our simple three-step process makes going solar easy and stress-free.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative"
              >
                <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all">
                  <div className="text-6xl font-bold text-energy-yellow/20 mb-4">{step.number}</div>
                  <h3 className="text-2xl font-bold text-solar-blue mb-3">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <i className="ri-arrow-right-line text-3xl text-energy-yellow"></i>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-6">
                Why Choose <strong>BRICK CITY SOLAR</strong>?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                As Ocala's premier solar energy provider, we combine local expertise with industry-leading technology to deliver exceptional results.
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center bg-eco-green/10 rounded-lg mr-4 flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-2xl text-eco-green"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-solar-blue mb-2">Local Ocala Experts</h4>
                    <p className="text-sm text-gray-600">We understand Florida's unique climate and energy needs, providing tailored solar solutions.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center bg-eco-green/10 rounded-lg mr-4 flex-shrink-0">
                    <i className="ri-award-line text-2xl text-eco-green"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-solar-blue mb-2">Certified Professionals</h4>
                    <p className="text-sm text-gray-600">Our team consists of licensed, certified solar installers with years of experience.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center bg-eco-green/10 rounded-lg mr-4 flex-shrink-0">
                    <i className="ri-customer-service-2-line text-2xl text-eco-green"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-solar-blue mb-2">Lifetime Support</h4>
                    <p className="text-sm text-gray-600">We provide ongoing maintenance, monitoring, and support for the life of your system.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center bg-eco-green/10 rounded-lg mr-4 flex-shrink-0">
                    <i className="ri-hand-coin-line text-2xl text-eco-green"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-solar-blue mb-2">Flexible Financing</h4>
                    <p className="text-sm text-gray-600">Multiple payment options and financing plans to fit any budget.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20solar%20panel%20installation%20team%20working%20on%20residential%20rooftop%2C%20certified%20technicians%20in%20safety%20gear%20installing%20black%20solar%20panels%2C%20blue%20sky%20background%2C%20modern%20suburban%20home%2C%20expert%20craftsmanship%2C%20professional%20service%2C%20clean%20installation%2C%20high%20quality%20workmanship%2C%20bright%20daylight&width=800&height=1000&seq=why-choose-team&orientation=portrait"
                  alt="Professional solar installation team"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-energy-yellow p-6 rounded-xl shadow-xl">
                <div className="text-4xl font-bold text-solar-blue">500+</div>
                <div className="text-sm font-semibold text-solar-blue">Happy Customers</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-soft-gray">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-4">
              What Our <strong>Customers</strong> Say
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real stories from real customers who made the switch to solar energy.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-xl shadow-lg"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-energy-yellow text-lg"></i>
                  ))}
                </div>
                <p className="text-sm text-gray-600 mb-6 italic">&ldquo;{testimonial.text}&rdquo;</p>
                <div>
                  <div className="font-semibold text-solar-blue">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-solar-blue text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Start Saving with Solar?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
              Get your free solar consultation today and discover how much you can save with clean energy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/contact"
                className="bg-energy-yellow text-solar-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-all shadow-xl cursor-pointer whitespace-nowrap"
              >
                Get Free Quote
              </Link>
              <a
                href="tel:352-509-6933"
                className="bg-white text-solar-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all shadow-xl cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                352-509-6933
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;